import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import type { RegisterRequest } from "@/lib/api-types";
import { authErrorMessage } from "@/lib/auth-errors";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");

  const mut = useMutation({
    mutationFn: (body: RegisterRequest) =>
      api.post<{ ok: true }>("/auth/register", body),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name || !email || mut.isPending) return;
    mut.mutate({ name, email, company: company || undefined });
  }

  const err = authErrorMessage(mut.error);
  const canSubmit = name.length > 0 && email.length > 0 && !mut.isPending;

  if (mut.isSuccess) {
    return (
      <AuthShell>
        <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-brand" strokeWidth={1.5} />
          <h1 className="mt-4 font-display text-3xl font-black tracking-tight">
            Request received
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We've sent a confirmation to <span className="text-foreground">{email}</span>.
            A platform administrator will review your request. You'll get an email once
            your workspace is provisioned.
          </p>
          <Button asChild variant="outline" className="mt-6">
            <Link to="/">Back home</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        <h1 className="font-display text-3xl font-black tracking-tight">
          Request access
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          imgwala Lab is invite-first. Tell us who you are — we'll get back to you.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reg-name">Your name</Label>
            <Input
              id="reg-name"
              type="text"
              autoComplete="name"
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={mut.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-email">Work email</Label>
            <Input
              id="reg-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={mut.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-company">
              Company{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="reg-company"
              type="text"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={mut.isPending}
            />
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="group w-full"
            disabled={!canSubmit}
          >
            {mut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send request
                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="mt-6 text-center text-sm text-muted-foreground">
        Already have access?{" "}
        <Link
          to="/login"
          className="font-medium text-foreground transition-colors hover:text-brand"
        >
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}
